// Reembolso de gastos: selecciona comprobantes RECIBIDOS (gastos de terceros) que
// se reembolsan en una factura por emitir. Su base+IVA se suma al total y va en el
// nodo <reembolsos> del XML SRI. Se guarda con fe_reembolso_guardar.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, Save, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { Modal, fieldCls } from '@/features/inventario/shared';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

interface Recibido {
  id: string; ruc_emisor: string | null; razon_social_emisor: string | null; serie_comprobante: string | null;
  tipo_comprobante: string | null; fecha_emision: string | null; importe_total: number; base: number; iva: number;
  proyecto: string | null; centro: string | null;
}
interface Asignado { ticket_buy_id: string; base: number; iva: number; proveedor: string | null; ruc: string | null; serie: string | null; fecha: string | null }
interface Validacion { ok: boolean; errores: string[]; propio: number; reembolso: number; total_esperado: number; importe_total: number }
interface FiltroOpt { id: string; nombre: string }
interface Filtros { proyectos: FiltroOpt[]; centros: FiltroOpt[] }

export function ReembolsoModal({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [buscar, setBuscar] = useState('');
  const [q, setQ] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [sel, setSel] = useState<Set<string> | null>(null);

  const { data: filtros } = useQuery<Filtros>({
    queryKey: ['fe-reembolso-filtros'],
    queryFn: async () => (await apiClient.post('/rpc/fe_reembolso_filtros', {}, FIN)).data,
  });
  const { data: recibidos = [], isFetching } = useQuery<Recibido[]>({
    queryKey: ['fe-reembolso-recibidos', q, proyectoId, centroId],
    queryFn: async () => (await apiClient.post('/rpc/fe_reembolso_recibidos',
      { _buscar: q || null, _limite: 60, _proyecto: proyectoId || null, _centro: centroId || null }, FIN)).data,
  });
  const { data: asignados = [] } = useQuery<Asignado[]>({
    queryKey: ['fe-reembolso-de', ticketId],
    queryFn: async () => (await apiClient.post('/rpc/fe_reembolso_de_factura', { _ticket_id: ticketId }, FIN)).data,
  });

  // 973: cuadre del reembolso guardado (el mismo control que exige Emitir)
  const { data: valid } = useQuery<Validacion>({
    queryKey: ['fe-reembolso-validar', ticketId],
    queryFn: async () => (await apiClient.post('/rpc/fe_reembolso_validar', { _ticket_id: ticketId }, FIN)).data,
    enabled: asignados.length > 0,
  });

  // Selección: parte de lo ya asignado; una vez que el usuario toca, manda `sel`.
  const seleccion = sel ?? new Set(asignados.map((a) => a.ticket_buy_id));
  const toggle = (id: string) => { const s = new Set(seleccion); s.has(id) ? s.delete(id) : s.add(id); setSel(s); };
  const idsAsignados = new Set(asignados.map((a) => a.ticket_buy_id));
  const desmarcarTodos = () => { const s = new Set(seleccion); for (const a of asignados) s.delete(a.ticket_buy_id); setSel(s); };
  // El buscador solo muestra recibidos NO asignados (los asignados van arriba,
  // donde siempre se pueden desmarcar aunque no entren en el límite de búsqueda)
  const disponibles = recibidos.filter((r) => !idsAsignados.has(r.id));

  // Total estimado (base+IVA) de lo seleccionado que esté visible o ya asignado.
  const montos = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of asignados) m.set(a.ticket_buy_id, Number(a.base) + Number(a.iva));
    for (const r of recibidos) m.set(r.id, Number(r.base) + Number(r.iva));
    return m;
  }, [asignados, recibidos]);
  const total = [...seleccion].reduce((s, id) => s + (montos.get(id) || 0), 0);

  const guardar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/fe_reembolso_guardar', { _ticket_id: ticketId, _ids: [...seleccion] }, FIN)).data,
    onSuccess: (r: any) => {
      toast.success(`Reembolsos guardados: ${r?.reembolsos ?? 0} comprobante(s) · ${usd(Number(r?.total_reembolso) || 0)}`);
      void qc.invalidateQueries({ queryKey: ['fac-facturas'] });
      void qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? '').startsWith('fe-reemb') });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo guardar'),
  });

  return (
    <Modal title="Reembolso de gastos — comprobantes recibidos" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          Elige los comprobantes <b>recibidos</b> (gastos de terceros) a reembolsar en esta factura. Su base + IVA se
          suma al total y viaja en el bloque <code>&lt;reembolsos&gt;</code> del SRI. Guarda <b>antes</b> de emitir. Se puede cambiar mientras la factura está por emitir, rechazada, devuelta o con error (al guardar vuelve a <b>por emitir</b> para reenviarla). Las compras de una factura anulada, descartada o acreditada por completo vuelven a estar disponibles.
        </p>
        {asignados.length > 0 && valid && (valid.ok ? (
          <p className="flex items-center gap-1.5 rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> El reembolso cuadra: propio {usd(valid.propio)} + reembolso {usd(valid.reembolso)} = total a cobrar {usd(valid.importe_total)}. Se puede emitir.
          </p>
        ) : (
          <div className="rounded border border-red-300 bg-red-50 px-2 py-1.5 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
            <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4 shrink-0" /> El reembolso NO cuadra: la factura no se podrá emitir hasta corregirlo.</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-6">{valid.errores.map((e) => <li key={e}>{e}</li>)}</ul>
            <p className="mt-1">Casi siempre se corrige volviendo a pulsar «Guardar reembolsos» (copia de nuevo los valores de las compras y recalcula el total).</p>
          </div>
        ))}
        {asignados.length > 0 && (
          <div className="rounded border bg-muted/30 p-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">
                En esta factura: {asignados.filter((a) => seleccion.has(a.ticket_buy_id)).length} de {asignados.length} marcado(s)
              </span>
              <Button size="sm" variant="ghost" onClick={desmarcarTodos}>Desmarcar todos</Button>
            </div>
            <div className="max-h-40 overflow-auto space-y-0.5">
              {asignados.map((a) => (
                <label key={a.ticket_buy_id} className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={seleccion.has(a.ticket_buy_id)} onChange={() => toggle(a.ticket_buy_id)} />
                  <span className={seleccion.has(a.ticket_buy_id) ? '' : 'line-through text-muted-foreground'}>
                    {a.proveedor ?? '—'}{a.serie ? ` · ${a.serie}` : ''}{a.fecha ? ` · ${String(a.fecha).slice(0, 10)}` : ''} · {usd(Number(a.base) + Number(a.iva))}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Desmarca el comprobante y presiona Guardar para RETIRARLO del reembolso de esta factura.</p>
          </div>
        )}
        <div className="flex gap-2">
          <input className={`${fieldCls} w-full`} placeholder="Buscar por proveedor, RUC o serie…" value={buscar}
            onChange={(e) => setBuscar(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setQ(buscar); }} />
          <Button size="sm" variant="outline" onClick={() => setQ(buscar)}><Search className="w-4 h-4" /></Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${fieldCls} w-56`} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
            <option value="">Todos los proyectos</option>
            {(filtros?.proyectos ?? []).map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
          <select className={`${fieldCls} w-56`} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
            <option value="">Todos los centros de costo</option>
            {(filtros?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          {(proyectoId || centroId) && <Button size="sm" variant="ghost" onClick={() => { setProyectoId(''); setCentroId(''); }}>Limpiar</Button>}
        </div>
        <div className="max-h-80 overflow-auto rounded border">
          {isFetching && <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background"><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 px-2"></th><th className="py-1 px-2">Proveedor</th><th className="py-1 px-2">Serie</th>
              <th className="py-1 px-2">Fecha</th><th className="py-1 px-2 text-right">Base</th><th className="py-1 px-2 text-right">IVA</th>
            </tr></thead>
            <tbody>
              {disponibles.length === 0 && !isFetching && <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">Sin comprobantes recibidos</td></tr>}
              {disponibles.map((r) => (
                <tr key={r.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/40" onClick={() => toggle(r.id)}>
                  <td className="py-1 px-2"><input type="checkbox" checked={seleccion.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} /></td>
                  <td className="py-1 px-2">{r.razon_social_emisor || '—'}
                    <span className="block font-mono text-[10px] text-muted-foreground">{r.ruc_emisor} · {r.tipo_comprobante}</span>
                    {(r.proyecto || r.centro) && <span className="block text-[10px] text-muted-foreground">{[r.proyecto && `Proy: ${r.proyecto}`, r.centro && `CC: ${r.centro}`].filter(Boolean).join(' · ')}</span>}
                  </td>
                  <td className="py-1 px-2 font-mono text-xs">{r.serie_comprobante || '—'}</td>
                  <td className="py-1 px-2 text-xs">{r.fecha_emision?.slice(0, 10) || '—'}</td>
                  <td className="py-1 px-2 text-right font-mono">{usd(r.base)}</td>
                  <td className="py-1 px-2 text-right font-mono">{usd(r.iva)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">{seleccion.size} seleccionado(s) · reembolso ≈ <b>{usd(total)}</b></span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="mr-1 h-4 w-4" /> Guardar reembolsos</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
